/**
 * ClientAdsTab — Google Ads sub-tab inside ClientDetailPanel.
 *
 * Two states:
 *   1. Unbound — empty-state CTA with the AdsAccountPicker. Save flips
 *      the customer's googleAdsCustomerId field in Firestore so this
 *      tab and every other Ads-aware surface (true-ROAS widget, Andrei
 *      tool handlers in Phase 4, etc.) start working immediately.
 *   2. Bound — KPIs / campaigns / daily for the bound CID, identical
 *      schema to the marketing page but scoped to one account. Includes
 *      a [change / unbind] control in the corner.
 *
 * Loading + error handling mirrors the marketing page so the UX feels
 * consistent. Doesn't duplicate the manager / auto-eject logic from
 * GoogleAdsPage — by design the binding picker disables managers, so
 * by the time we hit this tab the bound CID is always a leaf.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../../firebase';
import { Customer } from '../../customers/CustomerInterfaces';
import { TrueRoasWidget } from './TrueRoasWidget';
import {
  CampaignRow,
  CustomerSummaryRow,
  DailyPerformanceRow,
  callAdsQuery,
  formatCustomerId,
  microsToAmount,
} from '../../marketing/googleAdsApi';
import { AdsAccountPicker } from '../../marketing/AdsAccountPicker';

interface ClientAdsTabProps {
  client: Customer;
}

// ─────────────────────────────────────────────────────────────────────
// Formatters (kept local so this tab stays self-contained — the
// marketing page has its own copies for now)
// ─────────────────────────────────────────────────────────────────────

function n(v: string | number | undefined | null): number {
  if (v === undefined || v === null || v === '') return 0;
  const num = Number(v);
  return isFinite(num) ? num : 0;
}

function formatCurrency(amount: number, currency?: string): string {
  if (!currency) {
    return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatInt(amount: number): string {
  return new Intl.NumberFormat('en-AU').format(Math.round(amount));
}

function formatDecimal(amount: number, digits = 2): string {
  return new Intl.NumberFormat('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(2)}%`;
}

function lower(s: string | undefined): string {
  return String(s || '').toLowerCase().replace(/_/g, ' ');
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export const ClientAdsTab: React.FC<ClientAdsTabProps> = ({ client }) => {
  const boundCid = (client.googleAdsCustomerId || '').trim();
  const boundName = client.googleAdsCustomerName || '';

  // ── Bind / unbind / change state ───────────────────────────────
  // showPicker drives both the initial empty-state CTA AND the
  // "change binding" affordance shown when already bound. saving is a
  // brief lock during the Firestore write to avoid double-clicks.
  const [showPicker, setShowPicker] = useState(false);
  const [pickerValue, setPickerValue] = useState('');
  const [pickerName, setPickerName] = useState('');
  const [saving, setSaving] = useState(false);

  const persistBinding = useCallback(async (newCid: string, newName: string) => {
    if (!client.id) return;
    setSaving(true);
    try {
      const updates: Record<string, any> = {
        googleAdsCustomerId: newCid || null,
        googleAdsCustomerName: newCid ? (newName || '') : null,
        googleAdsBoundAt: newCid ? new Date().toISOString() : null,
        // serverTimestamp for sort/index parity with other audit fields.
        // (No-op for the rest of the doc; the updateDoc only touches
        // these four fields.)
        _adsBindingUpdatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'customers', client.id), updates);
      toast.success(newCid ? 'Google Ads account bound' : 'Google Ads account unbound');
      setShowPicker(false);
    } catch (e: any) {
      console.error('[ClientAdsTab] persistBinding failed:', e);
      toast.error(`Could not save binding: ${e?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  }, [client.id]);

  // ── Data fetch (bound state) ──────────────────────────────────
  const [summary, setSummary] = useState<CustomerSummaryRow | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [daily, setDaily] = useState<DailyPerformanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Race-id guard: a slow response for an old binding can't clobber a
   *  new one. Same pattern the marketing page uses. */
  const runIdRef = useRef(0);

  const loadData = useCallback(async (cid: string) => {
    if (!cid) return;
    const myRun = ++runIdRef.current;
    setLoading(true);
    setError(null);
    setSummary(null);
    setCampaigns([]);
    setDaily([]);
    try {
      const [sumResp, campResp, dailyResp] = await Promise.all([
        callAdsQuery<CustomerSummaryRow>(cid, 'customer_summary'),
        callAdsQuery<CampaignRow>(cid, 'campaigns_last_30_days'),
        callAdsQuery<DailyPerformanceRow>(cid, 'daily_performance_last_30_days'),
      ]);
      if (runIdRef.current !== myRun) return;
      const errors: string[] = [];
      if (sumResp.ok && sumResp.rows) setSummary(sumResp.rows[0] || null);
      else if (!sumResp.ok) errors.push(`summary: ${sumResp.error || `HTTP ${sumResp.httpStatus ?? '???'}`}`);
      if (campResp.ok && campResp.rows) setCampaigns(campResp.rows);
      else if (!campResp.ok) errors.push(`campaigns: ${campResp.error || `HTTP ${campResp.httpStatus ?? '???'}`}`);
      if (dailyResp.ok && dailyResp.rows) setDaily(dailyResp.rows);
      else if (!dailyResp.ok) errors.push(`daily: ${dailyResp.error || `HTTP ${dailyResp.httpStatus ?? '???'}`}`);
      if (errors.length > 0) setError(errors.join(' · '));
    } catch (e: any) {
      if (runIdRef.current !== myRun) return;
      setError(e?.message || 'Could not load Google Ads data');
    } finally {
      if (runIdRef.current === myRun) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (boundCid) void loadData(boundCid);
  }, [boundCid, loadData]);

  // ── Derived KPIs ──────────────────────────────────────────────
  const kpis = useMemo(() => {
    let cost = 0, impressions = 0, clicks = 0, conversions = 0, conversionsValue = 0;
    for (const row of daily) {
      cost += microsToAmount(row.metrics?.costMicros);
      impressions += n(row.metrics?.impressions);
      clicks += n(row.metrics?.clicks);
      conversions += n(row.metrics?.conversions);
      conversionsValue += n(row.metrics?.conversionsValue);
    }
    return { cost, impressions, clicks, conversions, conversionsValue };
  }, [daily]);

  const currency = summary?.customer?.currencyCode || undefined;
  const displayName =
    summary?.customer?.descriptiveName ||
    boundName ||
    (boundCid ? formatCustomerId(boundCid) : '');

  // ── Empty state (no binding yet) ──────────────────────────────
  if (!boundCid && !showPicker) {
    return (
      <div className="card mb-5">
        <div className="card-body text-center py-10">
          <i className="bi bi-google fs-1 text-muted mb-3 d-block"></i>
          <h4 className="fw-bold mb-2">No Google Ads account bound</h4>
          <p className="text-muted mb-4">
            Link this customer to a Google Ads account to see live campaigns,
            performance, and true-ROAS (paid invoices ÷ Ads spend) directly here.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { setPickerValue(''); setShowPicker(true); }}
          >
            <i className="bi bi-link-45deg me-2"></i>
            Bind Google Ads account
          </button>
        </div>
      </div>
    );
  }

  // ── Picker state (binding or changing) ─────────────────────────
  if (showPicker) {
    return (
      <div className="card mb-5">
        <div className="card-body">
          <h4 className="fw-bold mb-3">
            {boundCid ? 'Change Google Ads binding' : 'Bind to Google Ads account'}
          </h4>
          <p className="text-muted">
            Pick the operating account that belongs to <strong>{client.name || client.company}</strong>.
            Managers and sub-managers are listed but not selectable — campaign and metric
            queries don't work on them.
          </p>
          <div className="mb-3">
            <AdsAccountPicker
              value={pickerValue || boundCid}
              onChange={(cid, name) => {
                setPickerValue(cid);
                setPickerName(name);
              }}
              placeholder="— pick an account —"
            />
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || (!pickerValue && !boundCid)}
              onClick={() => persistBinding(pickerValue, pickerName)}
            >
              {saving ? 'Saving…' : (pickerValue ? 'Save binding' : 'Save')}
            </button>
            {boundCid && (
              <button
                type="button"
                className="btn btn-light-danger"
                disabled={saving}
                onClick={() => persistBinding('', '')}
              >
                Unbind
              </button>
            )}
            <button
              type="button"
              className="btn btn-light"
              disabled={saving}
              onClick={() => setShowPicker(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Bound state — KPIs / campaigns / daily ────────────────────
  return (
    <div>
      {/* Account header */}
      <div className="card mb-5">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div className="fs-7 text-muted mb-1">Bound to Google Ads account</div>
            <div className="fw-bold fs-4">{displayName}</div>
            <div className="text-muted fs-7">
              <code>{formatCustomerId(boundCid)}</code>
              {currency && <> · {currency}</>}
              {summary?.customer?.timeZone && <> · {summary.customer.timeZone}</>}
              {summary?.customer?.testAccount && (
                <span className="badge badge-light-warning ms-2">test account</span>
              )}
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => loadData(boundCid)}
              disabled={loading}
              title="Re-fetch data"
            >
              {loading ? 'Loading…' : <><i className="bi bi-arrow-clockwise me-1"></i>Refresh</>}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => { setPickerValue(boundCid); setShowPicker(true); }}
            >
              <i className="bi bi-pencil me-1"></i>Change / unbind
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-light-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {/* True-ROAS widget */}
      <TrueRoasWidget
        customerId={client.id || ''}
        adsCustomerId={boundCid}
        adsCurrency={currency}
      />

      {/* KPI strip */}
      <div className="row g-3 mb-5">
        <Kpi label="Cost · 30d" value={formatCurrency(kpis.cost, currency)} loading={loading} />
        <Kpi label="Impressions · 30d" value={formatInt(kpis.impressions)} loading={loading} />
        <Kpi label="Clicks · 30d" value={formatInt(kpis.clicks)} loading={loading} />
        <Kpi label="Conversions · 30d" value={formatDecimal(kpis.conversions, 2)} loading={loading} />
        <Kpi label="Conv. value · 30d" value={formatCurrency(kpis.conversionsValue, currency)} loading={loading} />
        <Kpi
          label="ROAS · 30d"
          value={kpis.cost > 0 ? `${(kpis.conversionsValue / kpis.cost).toFixed(2)}x` : '—'}
          loading={loading}
        />
      </div>

      {/* Campaigns */}
      <div className="card mb-5">
        <div className="card-header">
          <h5 className="card-title fw-bold m-0">
            Campaigns · last 30 days
            {campaigns.length === 100 && (
              <span className="text-muted fs-7 fw-normal ms-2">(showing top 100 by cost)</span>
            )}
          </h5>
        </div>
        <div className="card-body p-0">
          {loading && campaigns.length === 0 ? (
            <div className="text-muted text-center py-5">Loading campaigns…</div>
          ) : campaigns.length === 0 ? (
            <div className="text-muted text-center py-5">No campaigns to show</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-row-bordered mb-0">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th>Name</th>
                    <th>Status</th>
                    <th>Channel</th>
                    <th className="text-end">Budget · daily</th>
                    <th className="text-end">Cost</th>
                    <th className="text-end">Impr.</th>
                    <th className="text-end">Clicks</th>
                    <th className="text-end">CTR</th>
                    <th className="text-end">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.campaign.id}>
                      <td className="fw-semibold">{c.campaign.name}</td>
                      <td><span className="badge badge-light">{lower(c.campaign.status)}</span></td>
                      <td className="text-muted">{lower(c.campaign.advertisingChannelType)}</td>
                      <td className="text-end">{formatCurrency(microsToAmount(c.campaignBudget?.amountMicros), currency)}</td>
                      <td className="text-end">{formatCurrency(microsToAmount(c.metrics?.costMicros), currency)}</td>
                      <td className="text-end">{formatInt(n(c.metrics?.impressions))}</td>
                      <td className="text-end">{formatInt(n(c.metrics?.clicks))}</td>
                      <td className="text-end">{formatPercent(n(c.metrics?.ctr))}</td>
                      <td className="text-end">{formatDecimal(n(c.metrics?.conversions), 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

const Kpi: React.FC<{ label: string; value: string; loading?: boolean }> = ({ label, value, loading }) => (
  <div className="col-6 col-md-4 col-lg-2">
    <div className="card h-100">
      <div className="card-body p-3">
        <div className="fs-8 text-muted text-uppercase">{label}</div>
        <div className="fs-4 fw-bold mt-1">{loading ? '…' : value}</div>
      </div>
    </div>
  </div>
);

export default ClientAdsTab;
